<script setup lang="ts">
import { createReusableTemplate } from "@vueuse/core"
import { X } from "lucide-vue-next"
import { ThemeButton } from "~/components/theme"
import { Drawer, DrawerContent, DrawerTrigger } from "~/components/ui/drawer"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover"
import { Separator } from "~/components/ui/separator"
import { useMedia } from "~/composables/media"

const { info } = defineProps<{
    info: string[]
}>()

const open = defineModel("open", { type: Boolean, default: false })

const emits = defineEmits<{
    (e: "confirm"): void
}>()

const { isDesktop } = useMedia()

const [UseInfoContent, InfoContent] = createReusableTemplate()
</script>

<template>
    <UseInfoContent>
        <div class="space-y-6">
            <header class="space-y-4">
                <h4 class="uppercase text-sm text-need-to-know-popover-text">
                    Need to Know
                </h4>
                <Separator class="w-10 bg-background/20" />
            </header>
            <ul class="text-sm space-y-2.5 text-need-to-know-popover-text">
                <li
                    v-for="li of info"
                    :key="li"
                >
                    {{ li }}
                </li>
            </ul>
            <ThemeButton
                type="button"
                size="lg"
                class="w-full bg-reservation-form-submit text-reservation-form-submit-text hover:bg-reservation-form-submit/80 min-w-40"
                @click="emits('confirm')"
            >
                Continue
            </ThemeButton>
        </div>
    </UseInfoContent>
    <Popover
        v-if="isDesktop"
        :open="open"
    >
        <PopoverContent
            side="top"
            class="relative bg-need-to-know-popover-background text-need-to-know-popover-text border-none w-[calc(var(--reka-popover-trigger-width)+3rem)] translate-y-[4.5rem] p-6"
            @interact-outside="open = false"
            @escape-key-down="open = false"
        >
            <ThemeButton
                type="button"
                variant="ghost"
                size="icon"
                class="absolute size-4 right-4 top-4 opacity-70 transition-opacity hover:opacity-100 hover:bg-transparent hover:text-background"
                @click="open = false"
            >
                <X class="size-4" />
            </ThemeButton>
            <InfoContent />
        </PopoverContent>
        <PopoverTrigger as-child>
            <slot />
        </PopoverTrigger>
    </Popover>
    <Drawer
        v-else
        :open="open"
        @close="open = false"
    >
        <DrawerContent
            class="[&>:first-child]:shrink-0 bg-need-to-know-popover-background text-need-to-know-popover-text border-none"
            @interact-outside="open = false"
        >
            <div class="p-4">
                <InfoContent />
            </div>
        </DrawerContent>
        <DrawerTrigger as-child>
            <slot />
        </DrawerTrigger>
    </Drawer>
</template>
